import { useMemo } from 'react';

import { type UseImageSourceProps } from './useImageSourceProps.types';

/**
 * Returns component props object with the image source and optional static
 * image URL that sets `data` attribute on the image container which can be
 * used to swap animated image with a static version on web platform.
 *
 * NOTE: This allows our a11y vendor to swap animated image with a static
 *       static version on web.
 *
 * NOTE: On native platforms we decide the source based on the "reduce motion"
 *       mode.
 *
 * @example
 *
 * const imageSourceProps = useImageSourceProps({
 *   source: 'https://example.com/animated-image.png',
 *   staticSourceUrl: 'https://example.com/static-image.png',
 * });
 *
 * <Image
 *   {...imageSourceProps}
 * />
 */
export const useImageSourceProps: UseImageSourceProps = (params) => {
  const { source, staticSourceUrl } = params;

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      source,
      dataSet: staticSourceUrl
        ? { ...getStaticSourceUrlProps(staticSourceUrl), ...EXPO_IMAGE_DATA_SET }
        : EXPO_IMAGE_DATA_SET,
    }),
    [source, staticSourceUrl],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getStaticSourceUrlProps(url: string) {
  return { staticSource: url };
}

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * NOTE: expo-image@57 lets a `dataSet` prop replace the `data-expoimage` marker
 *       its web transition styles are scoped to, so images pop in without the
 *       fade. Re-adding the marker restores it.
 *
 * NOTE: Remove this constant on Expo 58, since expo-image@58 merges the marker
 *       into the `dataSet` itself.
 *
 * @see {@link https://github.com/expo/expo/pull/48821}
 */
const EXPO_IMAGE_DATA_SET = { expoimage: 'true' };
